import { useEffect, useRef, useState } from 'react';

const processSteps = [
  {
    number: '01',
    title: 'Discovery & Research',
    description:
      'We immerse ourselves in your vision through in-depth consultations, analyzing requirements, budget parameters, and timelines to build a comprehensive brief.',
    icon: 'ri-search-eye-line',
    highlight: 'Deep-Dive Consultation',
  },
  {
    number: '02',
    title: 'Analysis & Strategy',
    description:
      'Our team conducts comprehensive market research across our global network, developing a strategic plan with curated options tailored to your goals and budget.',
    icon: 'ri-bar-chart-grouped-line',
    highlight: 'Data-Driven Planning',
  },
  {
    number: '03',
    title: 'Negotiation & Execution',
    description:
      'Leveraging decades of industry relationships, we negotiate the most favorable terms. Every contract detail is meticulously reviewed and optimized.',
    icon: 'ri-shake-hands-line',
    highlight: 'Best-In-Class Terms',
  },
  {
    number: '04',
    title: 'Implementation & Support',
    description:
      'From logistics coordination to onsite management, we oversee every element and deliver detailed post-event analysis for continuous improvement.',
    icon: 'ri-rocket-2-line',
    highlight: 'Flawless Delivery',
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

function StepCard({
  step,
  index,
  visible,
}: {
  step: (typeof processSteps)[0];
  index: number;
  visible: boolean;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`relative group transition-all duration-1000 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
      }`}
      style={{ transitionDelay: `${index * 180}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Giant background number */}
      <div
        className={`absolute -top-2 md:-top-4 left-0 right-0 flex justify-center pointer-events-none select-none transition-all duration-700 ${
          hovered ? 'opacity-30 scale-105' : 'opacity-[0.15]'
        }`}
      >
        <span
          className="text-[76px] md:text-[93px] lg:text-[110px] font-black leading-none tracking-tighter"
          style={{ fontFamily: 'Georgia, serif', fontVariantNumeric: 'tabular-nums', color: '#D5BA6B' }}
        >
          {step.number}
        </span>
      </div>

      {/* Card content */}
      <div className="relative z-10 pt-10 md:pt-14 h-full flex flex-col">
        {/* Icon + highlight tag */}
        <div className="flex items-center gap-3 mb-5">
          <div
            className={`w-11 h-11 flex items-center justify-center rounded-xl transition-all duration-500 ${
              hovered
                ? 'shadow-lg'
                : 'bg-white/[0.06] border border-white/10'
            }`}
            style={hovered ? { backgroundColor: '#D5BA6B', boxShadow: '0 10px 15px -3px rgba(213,186,107,0.3)' } : {}}
          >
            <i
              className={`${step.icon} text-lg transition-colors duration-500 ${
                hovered ? 'text-gray-900' : ''
              }`}
              style={!hovered ? { color: '#D5BA6B' } : {}}
            />
          </div>
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: 'rgba(213,186,107,0.7)' }}>
            {step.highlight}
          </span>
        </div>

        {/* Title */}
        <h4 className="text-xl md:text-2xl font-bold text-white mb-3 leading-tight min-h-[2.5em]">
          <a
            href="#process"
            className="transition-colors duration-300 cursor-pointer"
            style={{ color: 'inherit' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#D5BA6B'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'inherit'; }}
          >
            {step.title}
          </a>
        </h4>

        {/* Description */}
        <p className="text-base text-white/45 leading-relaxed font-light text-left">
          {step.description}
        </p>

        {/* Bottom accent line */}
        <div
          className={`mt-auto pt-6 h-[2px] rounded-full transition-all duration-700 origin-left ${
            hovered
              ? 'w-full opacity-100'
              : 'w-8 bg-white/10 opacity-60'
          }`}
          style={hovered ? { background: 'linear-gradient(to right, #D5BA6B, #B89A4A)' } : {}}
        />
      </div>

      {/* Arrow connector — hidden on last item and on mobile */}
      {index < processSteps.length - 1 && (
        <div className="hidden lg:flex absolute -right-6 top-1/2 translate-x-1/2 -translate-y-1/2 z-20 items-center justify-center">
          <div className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 bg-[#0a0a0a]">
            <i className="ri-arrow-right-s-line text-lg" style={{ color: 'rgba(213,186,107,0.6)' }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Process() {
  const header = useInView(0.15);
  const grid = useInView(0.05);
  const closing = useInView(0.15);

  return (
    <section
      id="process"
      className="relative py-28 md:py-36 overflow-hidden bg-gray-900"
    >
      {/* Ambient glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] rounded-full blur-[180px] pointer-events-none" style={{ backgroundColor: 'rgba(213,186,107,0.03)' }} />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full blur-[140px] pointer-events-none" style={{ backgroundColor: 'rgba(213,186,107,0.02)' }} />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={header.ref}
          className={`text-center mb-20 md:mb-28 transition-all duration-1000 ${
            header.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[2px]" style={{ background: 'linear-gradient(to right, transparent, #D5BA6B)' }} />
            <span className="text-[11px] uppercase tracking-[0.3em] font-semibold" style={{ color: '#D5BA6B' }}>
              How We Work
            </span>
            <div className="w-12 h-[2px]" style={{ background: 'linear-gradient(to left, transparent, #D5BA6B)' }} />
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5 tracking-tight">
            Our Process
          </h2>
          <p className="text-base text-white/50 leading-relaxed font-light max-w-xl mx-auto">
            A proven four-step methodology refined over 15&nbsp;years, designed to deliver
            exceptional results with precision and care.
          </p>
        </div>

        {/* 4-column grid */}
        <div ref={grid.ref} className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
          {/* Horizontal connector line — desktop only */}
          <div className="hidden lg:block absolute top-1/2 left-[8%] right-[8%] h-px pointer-events-none">
            <div
              className={`w-full h-full transition-all duration-[2500ms] origin-left ${
                grid.visible ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
              }`}
              style={{ background: 'linear-gradient(to right, rgba(213,186,107,0.2), rgba(255,255,255,0.06), rgba(213,186,107,0.2))' }}
            />
          </div>

          {processSteps.map((step, i) => (
            <StepCard key={step.number} step={step} index={i} visible={grid.visible} />
          ))}
        </div>

        {/* Closing Statement */}
        <div
          ref={closing.ref}
          className={`text-center mt-24 md:mt-32 transition-all duration-1000 ${
            closing.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-lg sm:text-xl font-semibold text-white/60 leading-relaxed max-w-3xl mx-auto mb-8">
            MGroup Global brings clarity, structure and seamless execution to every program - from first conversation to final delivery.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-8 py-4 text-gray-900 font-semibold rounded-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{ backgroundColor: '#D5BA6B', boxShadow: '0 10px 15px -3px rgba(213,186,107,0.2)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E0C87A'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(213,186,107,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#D5BA6B'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(213,186,107,0.2)'; }}
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Request a Consultation
            <i className="ri-arrow-right-line text-lg" />
          </a>
        </div>
      </div>
    </section>
  );
}
